import './ui/styles.css';
import { App, type PathKind } from './core/App';
import { loadSiteManifest } from './core/SiteManifest';
import type { GeoPoint } from './core/GeoMath';

/**
 * エントリポイント。機能判定 → パスA/B選択 → App起動(計画書§9)。
 *
 * クエリパラメータ:
 *   ?site=<id>        史跡ID(/sites/<id>/site.json)。省略時はデフォルト
 *   ?pose=xr|sensor|desktop  ポーズ供給源の強制指定
 *   ?debug=1          デバッグHUD表示
 *   ?geo=lat,lon      現在地の固定(デスクトップ検証用)
 *   ?origin=here      史跡原点を現在地で上書き(実機での動作確認用。
 *                     任意の場所でコンテンツを足元に出現させられる)
 */

const DEFAULT_SITE = 'pumpkin-bomb-kitaibaraki';

async function detectPath(forced: string | null): Promise<PathKind> {
  if (forced === 'xr' || forced === 'sensor' || forced === 'desktop') return forced;

  // パスA: WebXR immersive-ar(Android Chrome + ARCore)
  try {
    if (navigator.xr && (await navigator.xr.isSessionSupported('immersive-ar'))) {
      return 'xr';
    }
  } catch {
    /* isSessionSupported自体が失敗する環境はフォールバックへ */
  }
  // パスB: タッチ端末ならセンサーフォールバック(iOS Safari等)
  if (navigator.maxTouchPoints > 0 && 'DeviceOrientationEvent' in window) {
    return 'sensor';
  }
  return 'desktop';
}

function parseGeoOverride(raw: string | null): GeoPoint | null {
  if (!raw) return null;
  const [lat, lon] = raw.split(',').map(Number);
  if (lat === undefined || lon === undefined || Number.isNaN(lat) || Number.isNaN(lon)) return null;
  return { lat, lon };
}

async function boot(): Promise<void> {
  const container = document.getElementById('app');
  if (!container) throw new Error('#app が見つかりません');

  if (!window.isSecureContext) {
    throw new Error(
      'このアプリはHTTPS(またはlocalhost)でのみ動作します。位置情報・カメラ・センサーAPIにセキュアコンテキストが必要です。',
    );
  }

  const params = new URLSearchParams(location.search);
  const siteId = params.get('site') ?? DEFAULT_SITE;
  const debug = params.get('debug') === '1';
  const geoOverride = parseGeoOverride(params.get('geo'));

  const [{ manifest, baseUrl }, path] = await Promise.all([
    loadSiteManifest(siteId),
    detectPath(params.get('pose')),
  ]);

  // 原点のアンカー先:
  //   origin.anchor='viewer' の史跡では導入画面にチェックボックスを出し、
  //   ユーザーが「実際の座標で体験する(=?origin=fixed相当)」を切り替えられる。
  //   ?origin=fixed … チェックボックスの初期状態をON(実座標)にする
  //   ?origin=here  … 常に現在地を原点にする(fixedアンカーの史跡でも強制)
  const originParam = params.get('origin');
  const anchorChoice =
    manifest.origin.anchor === 'viewer'
      ? {
          label: '実際の着弾地の緯度と経度に落ちた場合を体験する',
          defaultChecked: originParam === 'fixed',
        }
      : null;

  // 現在地を事前取得しておく(開始タップ内でawaitするとWebXRのユーザー操作
  // 制限に抵触するため)。?geo=lat,lon 指定時(デスクトップ検証)はその座標を使う
  let viewerOrigin: GeoPoint | null = null;
  if (anchorChoice || originParam === 'here') {
    viewerOrigin =
      geoOverride ??
      (await new Promise<GeoPoint>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
          reject,
          { enableHighAccuracy: true, timeout: 20000 },
        );
      }).catch((e: GeolocationPositionError) => {
        console.warn('[main] 現在地の事前取得に失敗(開始時に再判定):', e.message);
        return null;
      }));
  }

  // fixedアンカーの史跡への ?origin=here は従来通り即座に上書き(チェックボックスなし)
  if (originParam === 'here' && !anchorChoice && viewerOrigin) {
    manifest.origin.lat = viewerOrigin.lat;
    manifest.origin.lon = viewerOrigin.lon;
  }

  const app = new App({ container, manifest, baseUrl, path, debug, geoOverride, viewerOrigin, anchorChoice });
  app.init();
}

// PWA: 本番ビルドのみService Workerを登録(devでのキャッシュ混乱を避ける)
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((e) => {
      console.warn('[main] Service Worker登録に失敗:', e);
    });
  });
}

boot().catch((e: unknown) => {
  console.error(e);
  const el = document.createElement('div');
  el.className = 'intro';
  el.innerHTML = `<h1>ShisekiAR</h1><div class="error"></div>`;
  (el.querySelector('.error') as HTMLElement).textContent =
    e instanceof Error ? e.message : String(e);
  document.getElementById('app')?.appendChild(el);
});
